import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, Info } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWorld } from "@/context/WorldContext";
import { useOrigin } from "@/components/Origin";
import { api, formatApiError } from "@/lib/api";
import { buildFamily, ends, familyIds, focusSubset, layoutFamily, CARD_W } from "@/lib/family";
import { PanZoom } from "@/components/PanZoom";
import { FamilyLines } from "@/components/FamilyLines";
import { FamilyCard } from "@/components/FamilyCard";

const bodyOf = (p, sections) => ({ title: p.title, category: p.category, summary: p.summary, content: p.content, image: p.image, tags: p.tags, event_date: p.event_date, event_order: p.event_order, sections, gallery: p.gallery || [], blocks: p.blocks || [], pinned_quote: p.pinned_quote || null });

const withItem = (sections, title, key, item) => {
  const out = structuredClone(sections || []);
  let s = out.find((x) => x.title === title);
  if (!s) out.push((s = { title, facts: [] }));
  let f = s.facts.find((x) => x.key === key);
  if (!f) s.facts.push((f = { key, items: [] }));
  if (f.items.some((i) => i.page_id === item.page_id && (i.role || null) === (item.role || null))) return null;
  f.items.push(item);
  return out;
};

const Notice = ({ icon: Icon, tone, title, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border p-3 text-sm ${tone}`} data-testid={testId}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 w-full text-left"><Icon className="w-4 h-4 shrink-0" /> <span className="flex-1">{title}</span><ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      {open && <div className="mt-2 pl-6 space-y-1">{children}</div>}
    </div>
  );
};

export default function FamilyTree() {
  const { world, basePath, readOnly, reload, scope } = useWorld();
  const { pages, toggle } = useOrigin();
  const [focus, setFocus] = useState("");
  const [hi, setHi] = useState(null);
  const [strip, setStrip] = useState(true);
  const fam = useMemo(() => buildFamily(pages), [pages]);
  const view = useMemo(() => (focus && fam.byId.has(focus) ? focusSubset(fam, focus) : { ...fam, ids: familyIds(fam) }), [fam, focus]);
  const layout = useMemo(() => layoutFamily(view.ids, view.edges, view.couples, fam.byId), [view, fam.byId]);
  const loners = fam.chars.filter((c) => !familyIds(fam).includes(c.id));
  const neighbors = useMemo(() => {
    if (!hi) return null;
    const s = new Set([hi]);
    [...view.edges, ...view.couples].forEach((e) => { const [a, b] = ends(e); if (a === hi) s.add(b); if (b === hi) s.add(a); });
    return s;
  }, [hi, view]);

  const act = {
    addRelative: async (src, mode, target, role, note) => {
      try {
        const t = typeof target === "string"
          ? (await api.post(`/worlds/${world.id}/pages`, { title: target, category: "Characters", summary: "", content: "", image: null, tags: [], event_date: "", event_order: null, sections: [] }, { params: scope })).data
          : target;
        const sections = mode === "partner"
          ? withItem(src.sections, "Biographical Information", "Marital Status", { text: t.title, page_id: t.id, note, role: null })
          : withItem(src.sections, "Relationships", "Family", { text: t.title, page_id: t.id, note: "", role });
        if (!sections) { toast.error("That link already exists"); return; }
        await api.put(`/pages/${src.id}`, bodyOf(src, sections));
        await reload();
        toast.success(`${t.title} added to ${src.title}'s family`);
      } catch (e) { toast.error(formatApiError(e)); }
    },
    removeRecord: async (r) => {
      try {
        const sections = structuredClone(r.src.sections).map((s) => (s.title !== r.section ? s : {
          ...s, facts: s.facts.map((f) => (f.key !== r.fact ? f : { ...f, items: f.items.filter((i) => i.page_id !== r.target) })),
        }));
        await api.put(`/pages/${r.src.id}`, bodyOf(r.src, sections));
        await reload();
        toast.success("Family link removed");
      } catch (e) { toast.error(formatApiError(e)); }
    },
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-7xl space-y-5" data-testid="family-tree-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-2">Bloodlines</p>
          <h1 className="font-display text-4xl sm:text-5xl text-amber-50">Family tree</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {toggle}
          <Select value={focus || "all"} onValueChange={(v) => setFocus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="family-focus-select"><SelectValue placeholder="Focus on a character" /></SelectTrigger>
            <SelectContent className="bg-panel">
              <SelectItem value="all">Focus on a character…</SelectItem>
              {fam.chars.filter((c) => familyIds(fam).includes(c.id)).map((c) => <SelectItem key={c.id} value={c.id} data-testid={`family-focus-option-${c.id}`}>{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
          {focus && <button onClick={() => setFocus("")} className="text-sm rounded-full border border-line px-4 py-2 text-slate-300 hover:border-amber-500/40" data-testid="family-show-all-btn">Show all families</button>}
        </div>
      </div>
      {fam.missing.length > 0 && (
        <Notice icon={Info} tone="border-sky-500/30 bg-sky-500/5 text-sky-200" testId="family-missing-roles" title={`${fam.missing.length} family link${fam.missing.length > 1 ? "s have" : " has"} no role yet`}>
          {fam.missing.map((m, i) => <Link key={i} to={`${basePath}/p/${m.page.id}`} className="block text-slate-300 hover:text-amber-300">{m.page.title} → {m.target.title}</Link>)}
        </Notice>
      )}
      {fam.conflicts.length > 0 && (
        <Notice icon={AlertTriangle} tone="border-rose-500/30 bg-rose-500/5 text-rose-200" testId="family-conflicts" title={`${fam.conflicts.length} conflicting family link${fam.conflicts.length > 1 ? "s" : ""}`}>
          {fam.conflicts.map((c, i) => (
            <p key={i} className="text-slate-300">{c.pages.map((p, j) => <span key={p.id}>{j > 0 && " vs "}<Link to={`${basePath}/p/${p.id}`} className="hover:text-amber-300 underline underline-offset-2">{p.title}</Link></span>)} <span className="text-slate-500">· showing {c.winner.title}'s version</span></p>
          ))}
        </Notice>
      )}
      {view.ids.length > 12 && !focus && <p className="text-xs text-slate-500 sm:hidden">Tip: use “Focus on a character” for large families.</p>}
      {view.ids.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line p-14 text-center" data-testid="family-empty">
          <p className="font-display text-3xl text-amber-100">No family ties yet.</p>
          <p className="text-slate-500 mt-2">Link characters in Relationships → Family or Marital Status{readOnly ? "." : ", or click a character card below to add relatives."}</p>
          {!readOnly && fam.chars.length === 0 && <Link to={`${basePath}/new?category=Characters`} className="inline-block mt-3 text-amber-400 hover:underline" data-testid="family-create-character-link">Create your first character</Link>}
        </div>
      ) : (
        <PanZoom width={layout.width + 120} height={layout.height} fitKey={`${focus}-${view.ids.length}`}>
          {layout.groups.map((g, i) => (
            <div key={i} className="absolute rounded-2xl border border-line/60 bg-panel/30" style={{ left: -24, top: g.y - 8, width: Math.max(g.w, CARD_W * 2) + 48, height: g.h + 32 }} data-testid={`family-group-${i}`}>
              <h2 className="font-display text-2xl text-amber-200/90 px-6 pt-3">{focus ? `Focus: ${fam.byId.get(focus).title}` : g.title}</h2>
            </div>
          ))}
          <svg width={layout.width} height={layout.height} className="absolute inset-0 overflow-visible pointer-events-none"><FamilyLines fam={view} pos={layout.pos} hi={hi} /></svg>
          {[...layout.pos.entries()].map(([id, p], i) => (
            <FamilyCard key={id} i={i} page={fam.byId.get(id)} p={p} dim={neighbors && !neighbors.has(id)} onHover={setHi} readOnly={readOnly} basePath={basePath}
              chars={fam.chars} records={fam.records} byId={fam.byId} act={act} />
          ))}
        </PanZoom>
      )}
      {loners.length > 0 && (
        <div className="rounded-xl border border-line bg-panel" data-testid="family-loners">
          <button onClick={() => setStrip((s) => !s)} className="w-full flex items-center justify-between px-4 py-3 text-sm text-slate-300" data-testid="family-loners-toggle">
            <span>Not in a family tree yet <span className="text-slate-500">({loners.length})</span></span><ChevronDown className={`w-4 h-4 transition-transform ${strip ? "rotate-180" : ""}`} />
          </button>
          {strip && (
            <div className="relative flex flex-wrap gap-3 px-4 pb-4">
              {loners.map((c, i) => (
                <div key={c.id} className="relative" style={{ width: 132, height: 118 }}>
                  <FamilyCard i={i} page={c} p={{ x: 0, y: 0 }} onHover={() => {}} readOnly={readOnly} basePath={basePath} chars={fam.chars} records={fam.records} byId={fam.byId} act={act} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
